import React, { useCallback } from 'react';

import { parse, stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Chip, ChipColor, ChipSize } from '@pkg/components/Sections/Profile/Chip';
import { GROUPS_HOME_SEARCH_PARAMS_KEYS, useGroupsChipFilters, useMedia } from '@pkg/hooks';

import { isGroupItemOwnership } from '@app/client/common/structs/groups/groups';

import { ChipText, ClearAll, Close, Diamond, FilterChipsWrapper, FilterHeading } from './styles';

interface Props {
    total?: number;
    clearFilter?: () => void;
}

export const FilterChips = ({ total, clearFilter }: Props) => {
    const { filters, floorPriceFilters, filterType, filtersExist } = useGroupsChipFilters();
    const navigate = useNavigate();
    const location = useLocation();
    const { t: translate } = useTranslation(['yat_nft_manager', 'groups']);
    const theme = useTheme();
    const notMobile = useMedia(`(min-width: ${theme.breakpoints.mobile})`);

    const handleRemoveFilter = useCallback(
        (key, value) => {
            const query = parse(location.search);

            if (key === GROUPS_HOME_SEARCH_PARAMS_KEYS.collections || key === GROUPS_HOME_SEARCH_PARAMS_KEYS.members) {
                delete query[key];
            } else {
                query[key] = (query[key] as string)
                    .split(',')
                    .filter((val) => val !== value)
                    .join(',');
            }

            if (!query[key]) delete query[key];
            const route = stringify(query);
            navigate(`${location.pathname}?${route}`);
        },
        [location.pathname, location.search, navigate],
    );

    const handleRemoveTypeFilter = (value: string) => {
        clearFilter?.();
        handleRemoveFilter('filter', value);
    };

    const handleRemoveFloorPriceFilters = useCallback(
        (keys) => {
            const query = parse(location.search);
            keys.forEach((key: string) => {
                delete query[key];
            });
            const route = stringify(query);
            navigate(`${location.pathname}?${route}`);
        },
        [location.pathname, location.search, navigate],
    );

    const handleClearAll = useCallback(() => {
        const query = parse(location.search);

        Object.keys(query).forEach((key) => {
            if ((key !== 'filter' && notMobile) || !notMobile) {
                delete query[key];
            }
        });
        const route = stringify(query);
        navigate(`${location.pathname}?${route}`, { replace: true });

        if (!notMobile) clearFilter?.();
    }, [location.search, location.pathname, navigate, notMobile, clearFilter]);

    if (!filtersExist) return null;

    const heading =
        total != undefined
            ? translate('yat_nft_manager:yat_nft_manager_filter_chips', {
                  total: total?.toString(),
              })
            : '';

    const getFilterText = (value: string): string => {
        // This is required since filter is called "purchased" but label should be "bought"
        if (isGroupItemOwnership(value)) {
            return translate(`groups:groups_filter_ownership_${value.toLocaleLowerCase()}`);
        }
        return value;
    };

    const chipsMarkup = filters.map((filter, i) => {
        if (filter.values.length) {
            if (
                filter.key === GROUPS_HOME_SEARCH_PARAMS_KEYS.collections ||
                filter.key === GROUPS_HOME_SEARCH_PARAMS_KEYS.members
            ) {
                const filterLength = filter.values.length;
                const filterName = filterLength > 1 ? filter.key : filter.key.substring(0, filter.key.length - 1);

                return (
                    <Chip
                        key={filter.key + i}
                        color={ChipColor.Secondary}
                        size={ChipSize.Small}
                        onClick={() => handleRemoveFilter(filter.key, '')}
                    >
                        <Chip.Interior>
                            <ChipText>{`${filterLength} ${filterName}`}</ChipText>
                            <Chip.AttachedIcon circle>
                                <Close />
                            </Chip.AttachedIcon>
                        </Chip.Interior>
                    </Chip>
                );
            }

            return filter.values.map((value) => (
                <Chip
                    key={value}
                    color={ChipColor.Secondary}
                    size={ChipSize.Small}
                    onClick={() => handleRemoveFilter(filter.key, value)}
                >
                    <Chip.Interior>
                        <ChipText>{getFilterText(value)}</ChipText>
                        <Chip.AttachedIcon circle>
                            <Close />
                        </Chip.AttachedIcon>
                    </Chip.Interior>
                </Chip>
            ));
        }

        return null;
    });

    return (
        <FilterChipsWrapper>
            <FilterHeading>{heading}</FilterHeading>

            {filterType && (
                <Chip
                    color={ChipColor.Secondary}
                    size={ChipSize.Small}
                    onClick={() => handleRemoveTypeFilter(filterType)}
                >
                    <Chip.Interior>
                        <ChipText>{filterType}</ChipText>
                        <Chip.AttachedIcon>
                            <Close />
                        </Chip.AttachedIcon>
                    </Chip.Interior>
                </Chip>
            )}

            {floorPriceFilters && (
                <Chip
                    color={ChipColor.Secondary}
                    size={ChipSize.Small}
                    onClick={() => handleRemoveFloorPriceFilters(floorPriceFilters.keys)}
                >
                    <Chip.Interior>
                        <Chip.AttachedIcon>
                            <Diamond />
                        </Chip.AttachedIcon>
                        <ChipText>{floorPriceFilters.label}</ChipText>
                        <Chip.AttachedIcon circle>
                            <Close />
                        </Chip.AttachedIcon>
                    </Chip.Interior>
                </Chip>
            )}

            {chipsMarkup}

            <ClearAll onClick={handleClearAll}>
                {translate('yat_nft_manager:yat_nft_manager_filter_clear_all')}
            </ClearAll>
        </FilterChipsWrapper>
    );
};
